import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { YARNS } from "../lib/yarns";

/**
 * LoomBoard — a member's saved/pinned threads.
 *
 * Renders nothing when the loomboard is empty. Otherwise shows a small grid
 * of saved cards a member can revisit or remove. Browser-stored for v1.
 */
export default function LoomBoard({ saved = [], onRemove }) {
  if (!saved || saved.length === 0) return null;

  return (
    <section
      id="saved"
      className="relative py-20 md:py-24"
      data-testid="loom-board"
      aria-labelledby="loomboard-heading"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10 md:mb-12">
          <div className="max-w-xl">
            <p className="text-xs uppercase tracking-[0.22em] text-weave-teal font-medium mb-3">
              Your loomboard
            </p>
            <h2
              id="loomboard-heading"
              className="text-3xl md:text-4xl font-semibold tracking-tight text-[#1a1a1a] leading-[1.1]"
            >
              The threads you wanted to keep.
            </h2>
            <p className="mt-3 text-sm md:text-base text-[#3a3a3a]/85 leading-relaxed">
              Pinned cards live in your browser. Open this page on the same
              device and they're here, in the order you saved them.
            </p>
          </div>
          <span
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 text-sm font-medium text-[#3d2a14]"
            style={{ borderColor: "#3d2a14", backgroundColor: "#fdfaf5" }}
            data-testid="loomboard-count"
          >
            <span
              className="block w-2 h-2 rounded-full"
              style={{ backgroundColor: "#e93e36" }}
              aria-hidden
            />
            {saved.length} {saved.length === 1 ? "thread" : "threads"}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          <AnimatePresence mode="popLayout">
            {saved.map((card, i) => {
              const yarn = YARNS[card.yarn] || YARNS.centre;
              return (
                <motion.article
                  key={card.id}
                  layout
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.2) }}
                  className="relative rounded-2xl border-2 p-5 md:p-6 shadow-[0_6px_20px_rgba(60,40,10,0.06)]"
                  style={{
                    backgroundColor: yarn.pastel,
                    borderColor: "#3d2a14",
                  }}
                  data-testid={`saved-card-${card.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span
                      className="text-[10px] uppercase tracking-[0.2em] font-semibold inline-flex items-center gap-1.5"
                      style={{ color: yarn.accent }}
                    >
                      <span
                        className="block w-2 h-2 rounded-full"
                        style={{ backgroundColor: yarn.accent }}
                        aria-hidden
                      />
                      {yarn.label}
                    </span>
                    <button
                      type="button"
                      onClick={() => onRemove?.(card.id)}
                      aria-label="Remove from loomboard"
                      data-testid={`saved-remove-${card.id}`}
                      className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/70 hover:bg-white border-2 transition-colors focus:outline-none focus-visible:ring-2"
                      style={{ borderColor: "#3d2a14", color: "#3d2a14" }}
                    >
                      <X className="w-3.5 h-3.5" strokeWidth={2.4} />
                    </button>
                  </div>
                  <p
                    className="text-base md:text-[17px] leading-snug text-[#1a1a1a]"
                    style={{ fontFamily: "Rubik, sans-serif" }}
                  >
                    {card.yarn === "reflect" ? `“${card.text}”` : card.text}
                  </p>
                  {card.attribution && (
                    <p className="mt-3 text-xs font-light tracking-wide text-[#3a3a3a]/80">
                      — {card.attribution}
                    </p>
                  )}
                  {card.savedAt && (
                    <p className="mt-4 text-[10px] uppercase tracking-[0.18em] text-[#3a3a3a]/55">
                      Pinned {formatSavedAt(card.savedAt)}
                    </p>
                  )}
                </motion.article>
              );
            })}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

function formatSavedAt(iso) {
  try {
    const d = new Date(iso);
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    if (sameDay) return "today";
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return "yesterday";
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
    });
  } catch {
    return "earlier";
  }
}
